import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  booleanAttribute,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { ControlValueAccessor } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InvalidXMLRegex } from '@vinnova/validation';
import { isValid } from 'date-fns';
import {
  AsYouType,
  CountryCode,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import { InputType } from '../../../models/input-type.model';
import {
  getNumberFormattedForElement,
  removeSpacesAndReplaceComma,
} from './text-input.utils';

@UntilDestroy()
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: `input[vinnova-text-input]`,
  templateUrl: './text-input.component.html',
  styleUrls: ['./text-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TextInputComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor
{
  @Input({ transform: booleanAttribute }) set error(value: boolean) {
    this.elementRef.nativeElement.classList.toggle('invalid', value);
  }

  @Input({ transform: booleanAttribute }) set warning(value: boolean) {
    this.elementRef.nativeElement.classList.toggle('invalid-warning', value);
  }

  @Input({ transform: booleanAttribute }) set required(value: boolean) {
    if (value) {
      this.elementRef.nativeElement.setAttribute('aria-required', 'true');
    } else {
      this.elementRef.nativeElement.removeAttribute('aria-required');
    }
  }

  private _placeholder: string;
  @Input() set placeholder(value: string) {
    this.elementRef.nativeElement.placeholder = value ?? '';
    this._placeholder = value;
  }
  get placeholder() {
    return this._placeholder;
  }

  private _disabled: boolean;
  @Input() set disabled(value: boolean) {
    this._disabled = value;
  }

  get disabled() {
    return this._disabled;
  }

  @Input() selectedCountry: CountryCode;
  @Output() selectedCountryChange = new EventEmitter<CountryCode>();

  @Input() min: number;
  @Input() max: number;
  @Input() inputType: InputType;
  @Input() suffix: string;
  @Input() defaultValue: number;
  @Input() allowMinus: boolean;

  phoneListener: Event;
  value: string;
  undoValues: string[] = [];
  redoValues: string[] = [];
  currentCountryCode: string;
  mousedown: boolean;
  suffixEl: HTMLElement;
  hadZeroDeleted: boolean;
  oldNumberValueFormattedForElement: string;
  private readonly MAX_DECIMALS = 4;

  get isNumber() {
    return (
      this.inputType === InputType.Amount ||
      this.inputType === InputType.Number ||
      this.inputType === InputType.Percent
    );
  }

  private onFocusReturnedToApplicationBound =
    this.onFocusReturnedToApplication.bind(this);

  constructor(
    public elementRef: ElementRef,
    private translocoService: TranslocoService,
    public ngControl: NgControl,
    private zone: NgZone,
  ) {
    ngControl.valueAccessor = this;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange = (_value: string | number) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  writeValue(value): void {
    if (this.inputType === InputType.Phone && value?.length > 1) {
      const phoneNumber = parsePhoneNumberWithError(
        value,
        this.selectedCountry,
      );
      const formattedValue = phoneNumber.isValid()
        ? phoneNumber.formatNational()
        : value;
      this.elementRef.nativeElement.value = formattedValue ?? '';
      this.value = value;
    } else if (this.isNumber) {
      const formattedValue =
        value === undefined || value === null || value === ''
          ? ''
          : this.formatNumber(value);
      this.elementRef.nativeElement.value = formattedValue;
      this.value = formattedValue;
    } else {
      this.elementRef.nativeElement.value = value ?? '';
      this.value = value;
    }
  }

  registerOnChange(fn: (value) => void): void {
    this.onChange = (value) => {
      if (typeof value === 'string') {
        value = value?.replaceAll(InvalidXMLRegex, '');
      }
      fn(value);
    };
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(disabled: boolean): void {
    //disabled som input kommer väga tyngre än när man disablar controllern.
    this.elementRef.nativeElement.disabled = this.disabled ?? disabled;
  }

  ngOnInit() {
    this.elementRef.nativeElement.classList.add('form-control', 'vinnova-fc');
    this.setMask();

    this.translocoService.langChanges$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.setMask();
        if (this.isNumber && this.value) {
          this.maskNumber(this.elementRef.nativeElement, this.allowMinus);
        }
      });

    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        if (this.suffix) {
          this.setSuffix();
        }
      });
    });

    if (this.inputType === InputType.Percent && !this.min) {
      this.min = 0;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedCountry'] && !changes['selectedCountry'].firstChange) {
      this.formatPhoneNumber(this.elementRef.nativeElement);
    }
    if (changes['inputType'] || changes['placeholder']) {
      this.setMask();
    }
    if (changes['suffix'] && !changes['suffix'].firstChange) {
      this.setSuffix();
    }
  }

  ngOnDestroy(): void {
    this.resetMask();
    this.elementRef.nativeElement.parentNode
      ?.querySelector('.suffix')
      ?.remove();
    this.suffixEl?.removeEventListener('click', this.suffixClickEvent);
  }

  @HostListener('mousedown')
  onMousedown() {
    if (this.elementRef.nativeElement === document.activeElement) {
      this.elementRef.nativeElement.classList.remove('focus-visible');
    }
    this.mousedown = true;
  }

  @HostListener('focusout')
  public onFocusOut() {
    if (document.hasFocus()) {
      // If focus still is in the application the user has left the element, clean elements value
      this.cleanElementValue();
    } else {
      window.addEventListener('focus', this.onFocusReturnedToApplicationBound);
    }
  }

  @HostListener('blur')
  onBlur() {
    if (
      this.inputType === InputType.Date &&
      !isValid(new Date(this.elementRef.nativeElement.value))
    ) {
      this.elementRef.nativeElement.value = '';
      this.value = undefined;
      this.onChange(this.value);
    }

    if (
      this.inputType === InputType.Number ||
      (this.inputType === InputType.Percent && this.min)
    ) {
      const number =
        typeof this.value === 'string'
          ? Number.parseFloat(removeSpacesAndReplaceComma(this.value))
          : this.value;
      if (number != undefined) {
        if (this.min && number < this.min) {
          this.elementRef.nativeElement.value = this.min.toString();
          this.value = this.min.toString();
          this.onChange(this.value);
        } else if (this.max && number > this.max) {
          this.elementRef.nativeElement.value = this.max.toString();
          this.value = this.max.toString();
          this.onChange(this.value);
        }
      }
    }

    if (this.inputType === InputType.Bankgiro) {
      this.formatBankgiro(this.elementRef.nativeElement);
    }
    if (this.inputType === InputType.PlusGiro) {
      this.formatPlusGiro(this.elementRef.nativeElement);
    }

    if (
      this.isNumber &&
      this.hadZeroDeleted &&
      this.inputType === InputType.Amount &&
      this.elementRef.nativeElement.value === ''
    ) {
      this.hadZeroDeleted = false;
      this.elementRef.nativeElement.value = '0';
    }

    this.onTouched();
    this.elementRef.nativeElement.classList.remove('focus-visible', 'focus');
  }

  @HostListener('focus')
  onFocus() {
    this.elementRef.nativeElement.classList.add('focus');
    if (!this.mousedown) {
      this.elementRef.nativeElement.classList.add('focus-visible');
    }
    this.mousedown = false;

    if (
      (this.inputType === InputType.Amount ||
        this.inputType === InputType.Date ||
        this.inputType === InputType.Diarienummer ||
        this.inputType === InputType.Number ||
        this.inputType === InputType.Organisationsnummer ||
        this.inputType === InputType.Percent ||
        this.inputType === InputType.Phone ||
        this.inputType === InputType.Zipcode) &&
      (this.undoValues.length === 0 ||
        this.undoValues.at(-1) !== this.elementRef.nativeElement.value)
    ) {
      this.undoValues.push(this.elementRef.nativeElement.value);
    }

    if (this.isNumber) {
      if (
        this.inputType === InputType.Amount &&
        this.elementRef.nativeElement.value === '0'
      ) {
        this.hadZeroDeleted = true;
        this.elementRef.nativeElement.value = '';
      } else {
        this.setCaretEnd(this.elementRef.nativeElement);
      }
    }
  }

  @HostListener('keydown.arrowup')
  onKeyUp() {
    if (
      this.inputType === InputType.Number ||
      this.inputType === InputType.Percent
    ) {
      this.elementRef.nativeElement.value = this.getSteppedValue(1).toString();
      this.maskNumber(
        this.elementRef.nativeElement,
        this.inputType === InputType.Number,
      );
    }
  }

  @HostListener('keydown.arrowdown')
  onKeyDown() {
    if (
      this.inputType === InputType.Number ||
      this.inputType === InputType.Percent
    ) {
      this.elementRef.nativeElement.value = this.getSteppedValue(-1).toString();
      this.maskNumber(
        this.elementRef.nativeElement,
        this.inputType === InputType.Number,
      );
    }
  }

  private getSteppedValue(step: number): number {
    if (!this.value) {
      return this.min ?? 0;
    }
    const currentValue =
      typeof this.value === 'string'
        ? Number.parseFloat(removeSpacesAndReplaceComma(this.value))
        : this.value;
    return currentValue + step;
  }

  @HostListener('beforeinput')
  onBeforeInput() {
    if (document.activeElement.classList.contains('focus-visible')) {
      this.elementRef.nativeElement.classList.add('focus');
      this.elementRef.nativeElement.classList.remove('focus-visible');
    }
  }

  @HostListener('input', ['$event'])
  onInput($event) {
    if (!this.elementRef.nativeElement.classList.contains('focus')) {
      this.elementRef.nativeElement.classList.add('focus');
    }
    this.redoValues = [];

    switch (this.inputType) {
      case InputType.Phone: {
        this.formatPhoneNumber($event.target);
        break;
      }
      case InputType.Number: {
        this.maskNumber($event.target, true);
        break;
      }
      case InputType.Percent:
      case InputType.Amount: {
        this.maskNumber($event.target, this.allowMinus);
        break;
      }
      case InputType.Diarienummer: {
        this.maskDiaryNumber($event.target);
        break;
      }
      case InputType.Organisationsnummer: {
        this.maskOrgNumber($event.target);
        break;
      }
      case InputType.Zipcode: {
        this.maskZipcode($event.target);
        break;
      }
      case InputType.Date: {
        this.maskDate($event.target);
        break;
      }
      default: {
        this.value = $event.target.value;
        this.onChange(this.value);
      }
    }
  }

  // Ctrl+z doesnt work on inputmask, hacky solution
  @HostListener('keydown.control.z', ['$event'])
  onUndo($event) {
    if (
      this.inputType === InputType.Amount ||
      this.inputType === InputType.Date ||
      this.inputType === InputType.Diarienummer ||
      this.inputType === InputType.Number ||
      this.inputType === InputType.Organisationsnummer ||
      this.inputType === InputType.Percent ||
      this.inputType === InputType.Phone ||
      this.inputType === InputType.Zipcode
    ) {
      const undoValue = this.undoValues.pop();
      if (undoValue !== undefined) {
        this.redoValues.push(this.elementRef.nativeElement.value);
        this.elementRef.nativeElement.value = undoValue;
        this.value = undoValue;
        if (this.isNumber) {
          const stripped = removeSpacesAndReplaceComma(this.value);
          const parsed = isNaN(stripped as unknown as number)
            ? undefined
            : parseFloat(stripped);
          this.onChange(parsed);
        } else {
          this.onChange(this.value);
        }
      }
      $event.preventDefault();
    }
  }

  // Shift+ctrl+z doesnt work on inputmask, hacky solution
  @HostListener('keydown.shift.control.z', ['$event'])
  onRedo($event) {
    if (
      this.inputType === InputType.Amount ||
      this.inputType === InputType.Date ||
      this.inputType === InputType.Diarienummer ||
      this.inputType === InputType.Number ||
      this.inputType === InputType.Organisationsnummer ||
      this.inputType === InputType.Percent ||
      this.inputType === InputType.Phone ||
      this.inputType === InputType.Zipcode
    ) {
      const redoValue = this.redoValues.pop();
      if (redoValue !== undefined) {
        this.undoValues.push(this.elementRef.nativeElement.value);
        this.elementRef.nativeElement.value = redoValue;
        this.value = redoValue;
        if (this.isNumber) {
          const stripped = removeSpacesAndReplaceComma(this.value);
          const parsed = isNaN(stripped as unknown as number)
            ? undefined
            : parseFloat(stripped);
          this.onChange(parsed);
        } else {
          this.onChange(this.value);
        }
      }
      $event.preventDefault();
    }
  }

  private setMask() {
    if (!this.inputType) {
      return;
    }
    this.resetMask();
    switch (this.inputType) {
      case InputType.Diarienummer: {
        this.elementRef.nativeElement.placeholder =
          this.translocoService.getActiveLang() === 'sv'
            ? 'ÅÅÅÅ-NNNNN'
            : 'YYYY-NNNNN';
        this.elementRef.nativeElement.style.textAlign = 'unset';
        this.elementRef.nativeElement.classList.add('has-mask');
        break;
      }
      case InputType.Organisationsnummer: {
        this.elementRef.nativeElement.placeholder = 'XXXXXX-XXXX';
        this.elementRef.nativeElement.style.textAlign = 'unset';
        this.elementRef.nativeElement.classList.add('has-mask');
        break;
      }
      case InputType.Date: {
        this.elementRef.nativeElement.placeholder =
          this.translocoService.getActiveLang() === 'sv'
            ? 'ÅÅÅÅ-MM-DD'
            : 'YYYY-MM-DD';
        this.elementRef.nativeElement.style.textAlign = 'unset';
        this.elementRef.nativeElement.classList.add('has-mask');
        break;
      }
      case InputType.Amount:
      case InputType.Percent:
      case InputType.Number: {
        this.elementRef.nativeElement.style.textAlign = 'right';
        this.elementRef.nativeElement.classList.add('has-mask');
        break;
      }
      case InputType.Zipcode:
      case InputType.Phone: {
        this.elementRef.nativeElement.style.textAlign = 'unset';
        this.elementRef.nativeElement.classList.add('has-mask');
        break;
      }
      default: {
        break;
      }
    }
  }

  private resetMask() {
    this.elementRef.nativeElement.placeholder = '';
    this.elementRef.nativeElement.style.textAlign = 'unset';
    this.elementRef.nativeElement.classList.remove('has-mask');
  }

  private formatPhoneNumber(el: HTMLInputElement) {
    const asYouType = new AsYouType(this.selectedCountry);
    asYouType.input(el.value);
    if (asYouType.country && asYouType.country !== this.selectedCountry) {
      this.selectedCountryChange.emit(asYouType.country);
    }

    if (asYouType.getNumber()) {
      const startPos = this.getCaretStart(el);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((asYouType as any).formatter?.template) {
        el.value = asYouType.input('');
      } else {
        const userHasEnteredCountryCallingCode =
          el.value.startsWith('+') || el.value.startsWith('00');
        const valueWithCountryCallingCode = userHasEnteredCountryCallingCode
          ? el.value
          : `+${asYouType.getNumber().countryCallingCode}${el.value}`;
        let formattedValue = new AsYouType(this.selectedCountry).input(
          valueWithCountryCallingCode,
        );
        if (!userHasEnteredCountryCallingCode) {
          const split = formattedValue.split(' ');
          split.shift();
          formattedValue = split.join(' ');
        }
        el.value = formattedValue;
      }
      this.setCaretStart(el, startPos);
    }

    this.value = el.value;
    this.onChange(this.value);
  }

  private setSuffix() {
    this.elementRef.nativeElement.parentNode.querySelector('.suffix')?.remove();
    if (!this.suffix) {
      return;
    }
    this.suffixEl = document.createElement('span');
    this.suffixEl.addEventListener('click', this.suffixClickEvent);
    this.suffixEl.innerHTML = this.suffix;
    this.suffixEl.classList.add('suffix');
    this.suffixEl.style.visibility = 'hidden';
    document.body.append(this.suffixEl);
    const width = this.suffixEl.offsetWidth;
    this.suffixEl.remove();
    this.suffixEl.style.visibility = 'unset';
    this.elementRef.nativeElement.parentNode.append(this.suffixEl);
    const widthRem = (14 + width + 4) / 16;
    this.elementRef.nativeElement.style.paddingRight = widthRem + 'rem';
  }

  private suffixClickEvent = () => {
    this.mousedown = true;
    this.elementRef.nativeElement.focus();
    this.elementRef.nativeElement.classList.add('focus');
  };

  private maskNumber(el: HTMLInputElement, allowMinus: boolean) {
    const allowDecimal = this.inputType === InputType.Number;
    let startPos = this.getCaretStart(el);
    let value = removeSpacesAndReplaceComma(el.value);
    // later logic assumes that value is not written as an exponential number
    if (isNaN(value as unknown as number) || value.includes('e')) {
      if (!allowMinus || value !== '-') {
        this.resetValue(el, startPos);
      }
    } else {
      let formattedValue: string;
      let parsedValue: number;
      if (value === '' && this.hadZeroDeleted) {
        formattedValue = '';
      } else if (value === '') {
        parsedValue = this.defaultValue;
        formattedValue =
          this.defaultValue === undefined || this.defaultValue === null
            ? ''
            : this.formatNumber(this.defaultValue);
      } else {
        if (value.includes('.')) {
          if (allowDecimal) {
            const decimalSplit = value.split('.');
            const decimals = decimalSplit[1].length;
            if (decimals > this.MAX_DECIMALS) {
              value =
                decimalSplit[0] +
                '.' +
                decimalSplit[1].slice(0, this.MAX_DECIMALS);
            }
          } else {
            this.resetValue(el, startPos);
            return;
          }
        }
        if (value.startsWith('-') && !allowMinus) {
          value = value.slice(1);
          startPos--;
        }
        parsedValue = Number.parseFloat(value);
        formattedValue = this.formatNumber(value);
      }
      el.value = formattedValue;
      this.value = this.formatNumber(parsedValue);

      this.oldNumberValueFormattedForElement =
        this.elementRef.nativeElement.value;
      this.onChange(parsedValue);
      this.setCaretStart(el, startPos);
    }
  }

  private formatNumber(value: number | string): string {
    const decimalSeparator =
      this.translocoService.getActiveLang() === 'en' ? '.' : ',';
    return getNumberFormattedForElement(
      value?.toString() ?? '',
      this.MAX_DECIMALS,
      decimalSeparator,
    );
  }

  private maskDiaryNumber(el: HTMLInputElement) {
    const pattern = /^(\d{0,9})$/;
    const startPos = this.getCaretStart(el);
    let value = el.value.replaceAll('-', '');
    if (pattern.test(value)) {
      if (value.length > 4) {
        value = value.slice(0, 4) + '-' + value.slice(4);
      }
      el.value = value;
      this.value = el.value;
      this.onChange(this.value);
      this.setCaretStart(el, startPos);
    } else {
      this.resetValue(el, startPos);
    }
  }

  private maskOrgNumber(el: HTMLInputElement) {
    const pattern = /^(\d{0,10})$/;
    const startPos = this.getCaretStart(el);
    let value = el.value.replaceAll('-', '');
    if (pattern.test(value)) {
      if (value.length > 6) {
        value = value.slice(0, 6) + '-' + value.slice(6);
      }
      el.value = value;
      this.value = el.value;
      this.onChange(this.value);
      this.setCaretStart(el, startPos);
    } else {
      this.resetValue(el, startPos);
    }
  }

  private maskZipcode(el: HTMLInputElement) {
    const pattern = /^(\d{0,5})$/;
    const startPos = this.getCaretStart(el);
    let value = el.value.replaceAll(/\s/g, '');
    if (pattern.test(value)) {
      if (value.length > 3) {
        value = value.slice(0, 3) + ' ' + value.slice(3);
      }
      el.value = value;
      this.value = el.value;
      this.onChange(this.value);
      this.setCaretStart(el, startPos);
    } else {
      this.resetValue(el, startPos);
    }
  }

  private maskDate(el: HTMLInputElement) {
    const pattern = /^(\d{0,8})$/;
    const startPos = this.getCaretStart(el);
    let value = el.value.replaceAll('-', '');
    if (pattern.test(value)) {
      if (value.length > 4) {
        value = value.slice(0, 4) + '-' + value.slice(4);
      }
      if (value.length > 7) {
        value = value.slice(0, 7) + '-' + value.slice(7);
      }
      el.value = value;

      this.value = el.value;
      this.setCaretStart(el, startPos);
      if (isValid(new Date(el.value))) {
        this.onChange(this.value);
      }
    } else {
      this.resetValue(el, startPos);
    }
  }

  private formatBankgiro(el: HTMLInputElement) {
    let value = el.value;
    if (
      isNaN(value as unknown as number) ||
      value.length < 7 ||
      value.length > 8
    ) {
      return;
    }

    value = value.slice(0, -4) + '-' + value.slice(-4);

    el.value = value;
    this.value = value;
    this.onChange(this.value);
  }

  private formatPlusGiro(el: HTMLInputElement) {
    let value = el.value;
    if (
      isNaN(value as unknown as number) ||
      value.length < 2 ||
      value.length > 8
    ) {
      return;
    }

    value = value.slice(0, -1) + '-' + value.slice(-1);

    el.value = value;
    this.value = value;
    this.onChange(this.value);
  }

  private resetValue(el: HTMLInputElement, startPos: number) {
    if (startPos) {
      startPos -= el.value.length - this.value.length;
    }
    if (
      this.inputType === InputType.Number ||
      this.inputType === InputType.Percent ||
      this.inputType === InputType.Amount
    ) {
      const value =
        this.oldNumberValueFormattedForElement &&
        removeSpacesAndReplaceComma(this.oldNumberValueFormattedForElement);

      const useOldValueFormattedForElement =
        this.value !== this.oldNumberValueFormattedForElement &&
        this.formatNumber(Number.parseFloat(value)) === this.value;

      el.value =
        (useOldValueFormattedForElement
          ? this.oldNumberValueFormattedForElement
          : this.value) ?? '';
    } else {
      el.value = this.value ?? '';
    }
    this.setCaretStart(el, startPos);
  }

  private getCaretStart(el: HTMLInputElement) {
    const selectionStart = el.selectionStart;
    const moveCaret = selectionStart !== el.value.length;
    return moveCaret ? selectionStart : undefined;
  }

  private setCaretStart(el: HTMLInputElement, startPos: number) {
    if (startPos >= 0) {
      el.selectionStart = startPos;
      el.selectionEnd = startPos;
    }
  }

  private setCaretEnd(el: HTMLInputElement) {
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        el.setSelectionRange(el.value.length, el.value.length);
      }, 100);
    });
  }

  private onFocusReturnedToApplication(): void {
    window.removeEventListener('focus', this.onFocusReturnedToApplicationBound);

    const clickedInside =
      this.elementRef.nativeElement === document.activeElement ||
      this.elementRef.nativeElement.contains(document.activeElement);

    if (!clickedInside) {
      // If focus is returned to application, but not to the element, clean element value
      this.cleanElementValue();
    }
  }

  /**
   * Clean the element value to remove any characters that might have been kept while writing.
   */
  private cleanElementValue(): void {
    if (
      this.inputType === InputType.Number ||
      this.inputType === InputType.Percent ||
      this.inputType === InputType.Amount
    ) {
      this.elementRef.nativeElement.value = this.value ?? '';
    }
  }
}
